/**
 * =============================================================================
 * PUBLIC QUESTION READ CACHE — ADR-028 Slice 3 (`BL-WIDGET-UXR-004`)
 * =============================================================================
 *
 * A session-scoped, MEMORY-ONLY stale-while-revalidate cache with in-flight
 * request de-duplication for the two public, moderation-gated reads:
 *
 *   - `GET /api/public/questions`      (browse / list)
 *   - `GET /api/public/questions/:id`  (detail)
 *
 * WHAT THIS DELIBERATELY IS NOT
 * -----------------------------
 * - NOT persistent. Nothing here touches `localStorage`, `sessionStorage`,
 *   IndexedDB, the Cache API, a service worker, or history state. State lives
 *   in module scope and dies with the document. A full page load starts cold.
 * - NOT a cache for anything visitor-specific. `/my-questions`, uploads,
 *   mutations, search, availability and config never reach this module. The
 *   only way data gets in is a response from the anonymous `credentials:'omit'`
 *   transport, so a cookie- or capability-influenced response cannot be keyed
 *   or admitted even by mistake.
 * - NOT a poller and NOT an optimistic writer. Nothing pending or moderated is
 *   ever inserted or represented as published.
 *
 * @see docs/decisions/adr-028-storefront-widget-experience-refresh.md §5 Slice 3, D-8
 * =============================================================================
 */

import {
  ID_LENGTHS,
  ID_PREFIXES,
  PUBLIC_RFI_STATE_LABELS,
  PUBLIC_VISIBLE_STATUSES,
  type ApiResponse,
  type PublicRfiItemType,
  type PublicVisibleStatus,
  type QuestionDetailResponse,
  type QuestionListResponse,
} from '@find-my-part/types';

import {
  buildPublicDetailEndpoint,
  buildPublicListEndpoint,
  getPublicReadScope,
  widgetApi,
  type PublicQuestionListOptions,
} from './client';

// =============================================================================
// RATIFIED NUMERIC POLICY
// =============================================================================

/**
 * ADR-028 requires "exported numeric fresh/stale TTL, entry and byte ceilings"
 * but specifies no values. These are the ratified Slice 3 numbers.
 *
 * They were chosen against MEASURED payload shapes, using the real DTOs and the
 * repository's own content limits (`CONTENT_VALIDATION`: title 200 chars,
 * description 10,000, reply body 10,000 — and the list DTO carries the
 * description UNTRUNCATED). Logical UTF-8 bytes of `JSON.stringify(data)`:
 *
 *   |                                             | bytes     |
 *   |---------------------------------------------|----------:|
 *   | Browse as actually requested (limit 6)      |     2,691 |
 *   | Browse, limit 6, rich descriptions          |     5,571 |
 *   | Server default page (limit 20)              |    18,396 |
 *   | Detail, 3 replies (typical)                 |     2,302 |
 *   | Detail, 10 replies                          |     9,803 |
 *   | Detail, 50 replies                          |    77,523 |
 *   | Detail, 200 max-length replies (worst case) | 2,038,973 |
 */
export const PUBLIC_READ_CACHE_POLICY = {
  /**
   * Below this age an entry renders with NO request at all.
   *
   * 30s comfortably spans a browse -> detail -> back -> another detail
   * exploration loop, which is exactly the ADR acceptance ("back/forward
   * between list and detail does not issue a blocking refetch within the
   * freshness window"). Public content is moderation-gated, so it changes on
   * human timescales and 30s of staleness is not observable to a visitor.
   */
  FRESH_TTL_MS: 30_000,

  /**
   * TOTAL maximum age, measured from the moment the entry was stored — not an
   * additional interval layered on top of freshness.
   *
   * `age < FRESH_TTL_MS`                    -> fresh   (serve, no request)
   * `FRESH_TTL_MS <= age < MAX_AGE_MS`      -> stale   (serve + revalidate)
   * `age >= MAX_AGE_MS`                     -> expired (blocking load)
   *
   * One stored timestamp and one subtraction decide all three, so there is no
   * second clock to drift.
   */
  MAX_AGE_MS: 300_000,

  /**
   * After a FAILED background revalidation, that key will not start another
   * one until this interval has passed. Bounds a persistently failing key to at
   * most one attempt per 10s instead of one attempt per render, which is what
   * turns a broken backend into a request storm.
   *
   * A SUCCESSFUL revalidation needs no cooldown: it makes the entry fresh
   * again, so `FRESH_TTL_MS` already suppresses the next attempt.
   */
  FAILED_REVALIDATION_COOLDOWN_MS: 10_000,

  /**
   * Maximum number of retained entries. Sized for a realistic session: a few
   * browse pages plus the questions a visitor actually opens. This ceiling
   * binds when many SMALL entries accumulate.
   */
  MAX_ENTRIES: 32,

  /**
   * Maximum logical bytes for a SINGLE entry. A response larger than this is
   * returned to the caller normally but never admitted.
   *
   * 128 KiB admits the measured 77.5 KB "very busy" detail and a rich
   * full-size list page, while refusing the multi-megabyte pathological
   * responses above. Without a per-entry ceiling one such response would evict
   * the entire working set to store a single page the visitor is already
   * looking at — strictly worse than not caching it.
   */
  MAX_ENTRY_BYTES: 131_072,

  /**
   * Maximum logical bytes across ALL entries. This ceiling binds when a few
   * LARGE entries accumulate; `MAX_ENTRIES` binds for many small ones, so both
   * are load-bearing and neither is dead code.
   */
  MAX_TOTAL_BYTES: 524_288,
} as const;

/**
 * Bumped whenever the cached DTO shape changes, so a cached payload produced by
 * an older contract can never be read back under a newer one.
 */
export const PUBLIC_READ_CACHE_KEY_VERSION = 'v1';

// =============================================================================
// BYTE ACCOUNTING
// =============================================================================

/**
 * Deterministic LOGICAL size of a cached payload: the UTF-8 byte length of its
 * canonical JSON serialisation.
 *
 * This is a cache bound, NOT a measurement of JavaScript heap usage — the real
 * retained object graph is larger and unknowable from script. Its value is that
 * it is deterministic, comparable across entries, multibyte-correct, and
 * reproducible in a test.
 *
 * Returns `null` for a payload that cannot be serialised (a cycle, or a BigInt).
 * An unmeasurable payload is never admitted.
 */
export function measureLogicalBytes(value: unknown): number | null {
  let json: string;
  try {
    json = JSON.stringify(value);
  } catch {
    return null;
  }
  if (typeof json !== 'string') return null;

  // TextEncoder is present in every browser this widget supports and in the
  // jsdom/node test environments; the fallback keeps the helper total.
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(json).length;

  /* c8 ignore next */
  return json.length;
}

// =============================================================================
// ADMISSION VALIDATION
// =============================================================================

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOwn(value: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return isFiniteNumber(value) && Number.isInteger(value) && value >= 0;
}

function isPositiveInteger(value: unknown): value is number {
  return isNonNegativeInteger(value) && value > 0;
}

function isPositiveSafeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

/**
 * Every timestamp consumed by the widget is passed to `Date#toISOString`,
 * which throws outside the ECMAScript time-value range. Reject those values at
 * the trust boundary instead of caching a render-crashing payload.
 */
function isEpochMilliseconds(value: unknown): value is number {
  return isFiniteNumber(value) && Math.abs(value) <= 8_640_000_000_000_000;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isOptionalString(record: Record<string, unknown>, key: string): boolean {
  return !hasOwn(record, key) || typeof record[key] === 'string';
}

function isOptionalFiniteNumber(record: Record<string, unknown>, key: string): boolean {
  return !hasOwn(record, key) || isFiniteNumber(record[key]);
}

function isPaginationMeta(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    isPositiveInteger(value.page) &&
    isPositiveInteger(value.limit) &&
    isNonNegativeInteger(value.total) &&
    isNonNegativeInteger(value.totalPages)
  );
}

const PUBLIC_VISIBLE_STATUS_SET = new Set<PublicVisibleStatus>(PUBLIC_VISIBLE_STATUSES);

function isPublicVisibleStatus(value: unknown): value is PublicVisibleStatus {
  return typeof value === 'string' && PUBLIC_VISIBLE_STATUS_SET.has(value as PublicVisibleStatus);
}

function isImageAttachment(value: unknown): boolean {
  if (!isRecord(value)) return false;

  const hasWidth = hasOwn(value, 'width');
  const hasHeight = hasOwn(value, 'height');
  if (hasWidth !== hasHeight) return false;
  if (hasWidth && (!isPositiveInteger(value.width) || !isPositiveInteger(value.height))) {
    return false;
  }

  return (
    typeof value.attachmentId === 'string' &&
    typeof value.url === 'string' &&
    isOptionalString(value, 'thumbnailUrl') &&
    isOptionalString(value, 'mimeType') &&
    isOptionalFiniteNumber(value, 'sizeMb') &&
    (!hasOwn(value, 'sizeMb') || (value.sizeMb as number) >= 0)
  );
}

function isPublicQuestionListItem(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    typeof value.questionId === 'string' &&
    typeof value.title === 'string' &&
    typeof value.slug === 'string' &&
    isNullableString(value.description) &&
    isPublicVisibleStatus(value.status) &&
    typeof value.posterName === 'string' &&
    isEpochMilliseconds(value.createdAt) &&
    isNonNegativeInteger(value.replyCount) &&
    isNonNegativeInteger(value.viewCount) &&
    typeof value.hasSolution === 'boolean' &&
    isNullableString(value.thumbnailUrl) &&
    typeof value.editoriallyCorrected === 'boolean'
  );
}

function isRecommendedProduct(value: unknown): boolean {
  if (!isRecord(value)) return false;

  let validPrice = true;
  if (hasOwn(value, 'price')) {
    const price = value.price;
    validPrice =
      isRecord(price) && isFiniteNumber(price.value) && typeof price.currencyCode === 'string';
  }

  return (
    isNonNegativeInteger(value.entityId) &&
    typeof value.name === 'string' &&
    typeof value.url === 'string' &&
    isOptionalString(value, 'imageUrl') &&
    validPrice
  );
}

function isPublicReply(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    typeof value.replyId === 'string' &&
    typeof value.authorName === 'string' &&
    (value.userType === 'ADMIN' || value.userType === 'CUSTOMER' || value.userType === 'GUEST') &&
    typeof value.body === 'string' &&
    typeof value.replyType === 'string' &&
    typeof value.isSolution === 'boolean' &&
    typeof value.editoriallyCorrected === 'boolean' &&
    Array.isArray(value.recommendedProducts) &&
    value.recommendedProducts.every(isRecommendedProduct) &&
    isEpochMilliseconds(value.createdAt) &&
    Array.isArray(value.images) &&
    value.images.every(isImageAttachment)
  );
}

function isPublicQuestionDetail(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    typeof value.questionId === 'string' &&
    typeof value.title === 'string' &&
    typeof value.slug === 'string' &&
    isNullableString(value.description) &&
    isPublicVisibleStatus(value.status) &&
    typeof value.posterName === 'string' &&
    Array.isArray(value.tags) &&
    value.tags.every((tag) => typeof tag === 'string') &&
    isEpochMilliseconds(value.createdAt) &&
    (value.resolvedAt === null || isEpochMilliseconds(value.resolvedAt)) &&
    isNonNegativeInteger(value.viewCount) &&
    isNonNegativeInteger(value.replyCount) &&
    typeof value.editoriallyCorrected === 'boolean' &&
    Array.isArray(value.images) &&
    value.images.every(isImageAttachment)
  );
}

function isQuestionSeoData(value: unknown): boolean {
  return (
    isRecord(value) &&
    isRecord(value.jsonLd) &&
    typeof value.jsonLdScript === 'string' &&
    typeof value.metaTags === 'string'
  );
}

function isSuggestedProduct(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    isNonNegativeInteger(value.productId) &&
    typeof value.name === 'string' &&
    typeof value.sku === 'string' &&
    isFiniteNumber(value.price) &&
    isOptionalFiniteNumber(value, 'salePrice') &&
    typeof value.url === 'string' &&
    isOptionalString(value, 'thumbnailUrl') &&
    isOptionalString(value, 'imageUrl') &&
    typeof value.isAvailable === 'boolean'
  );
}

const PUBLIC_RFI_ITEM_TYPES = new Set<PublicRfiItemType>([
  'photo',
  'measurement',
  'text',
  'selection',
  'confirmation',
]);
const PUBLIC_RFI_ITEM_STATES = new Set(Object.keys(PUBLIC_RFI_STATE_LABELS));

function isPublicRfiItem(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.label === 'string' &&
    typeof value.itemType === 'string' &&
    PUBLIC_RFI_ITEM_TYPES.has(value.itemType as PublicRfiItemType) &&
    typeof value.required === 'boolean' &&
    typeof value.state === 'string' &&
    PUBLIC_RFI_ITEM_STATES.has(value.state)
  );
}

function isPublicRfiRequest(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.message === 'string' &&
    isEpochMilliseconds(value.requestedAt) &&
    typeof value.state === 'string' &&
    PUBLIC_RFI_ITEM_STATES.has(value.state) &&
    Array.isArray(value.items) &&
    value.items.every(isPublicRfiItem)
  );
}

function isPublicRfiProjection(value: unknown): boolean {
  return (
    value === null ||
    (isRecord(value) &&
      typeof value.heading === 'string' &&
      Array.isArray(value.requests) &&
      value.requests.every(isPublicRfiRequest))
  );
}

function isStructuredFailureEnvelope(value: unknown): boolean {
  if (!isRecord(value) || value.success !== false || !isRecord(value.error)) return false;
  return typeof value.error.code === 'string' && typeof value.error.message === 'string';
}

function protocolErrorResponse<T>(message: string): ApiResponse<T> {
  return {
    success: false,
    error: { code: 'PROTOCOL_ERROR', message },
  };
}

/**
 * Bounded structural guard for a list payload.
 *
 * The transport casts the parsed body (`as ApiResponse<T>`) without validating
 * it, so TypeScript's `success: true` tells us nothing about runtime shape. A
 * truncated proxy response or a misconfigured intermediary can therefore
 * produce a "successful" envelope whose `data` is missing fields. Caching that
 * would turn a transient upstream fault into long-lived wrong state, so the
 * cache validates before admitting rather than trusting the cast.
 *
 * This guard covers every field the list renderer consumes, including the
 * timestamp range required by `Date#toISOString`. It deliberately allows
 * additional response fields for forward compatibility, but no required
 * render field may be absent or malformed.
 */
export function isCacheableListPayload(value: unknown): value is QuestionListResponse {
  if (!isRecord(value)) return false;
  if (!Array.isArray(value.questions)) return false;
  if (!isPaginationMeta(value.pagination)) return false;
  return value.questions.every(isPublicQuestionListItem);
}

/**
 * Complete runtime guard for the public detail contract. Detail rendering
 * indexes deeply into question, reply, image and RFI data, so accepting only a
 * question id is not a safe cache boundary: one malformed nested item can turn
 * a transient upstream fault into a repeatable render crash.
 */
export function isCacheableDetailPayload(value: unknown): value is QuestionDetailResponse {
  if (!isRecord(value)) return false;
  return (
    isPublicQuestionDetail(value.question) &&
    isQuestionSeoData(value.seo) &&
    Array.isArray(value.replies) &&
    value.replies.every(isPublicReply) &&
    Array.isArray(value.suggestedProducts) &&
    value.suggestedProducts.every(isSuggestedProduct) &&
    isPublicRfiProjection(value.rfi)
  );
}

/**
 * Bind a structurally valid list response to the request that produced it.
 *
 * The endpoint path is the cache key, but an intermediary can still return a
 * complete DTO for the wrong page/filter. Admitting that response would make a
 * key permanently represent another resource, so request semantics are checked
 * before either a cold caller or a stale revalidation can consume it.
 */
function isCacheableListPayloadForRequest(
  value: unknown,
  options: PublicQuestionListOptions
): value is QuestionListResponse {
  if (!isCacheableListPayload(value)) return false;

  // These are the Worker's wire defaults. `buildPublicListEndpoint` preserves
  // absence because the hook default (10) differs from the server default (20).
  const expectedPage = options.page ?? 1;
  const expectedLimit = options.limit ?? 20;
  if (!isPositiveSafeInteger(expectedPage) || !isPositiveSafeInteger(expectedLimit)) return false;
  if (expectedLimit > 100) return false;

  const { pagination, questions } = value;
  if (pagination.page !== expectedPage || pagination.limit !== expectedLimit) return false;
  if (questions.length > expectedLimit || pagination.total < questions.length) return false;
  if (pagination.totalPages !== Math.ceil(pagination.total / expectedLimit)) return false;

  // A page may legitimately be requested beyond the final page, but then it
  // must be empty. Any returned row consumes a position at the requested
  // offset, and that position must fit inside `total`. Keep every arithmetic
  // step safe so enormous attacker-controlled pagination values fail closed.
  const firstRowOffset = (expectedPage - 1) * expectedLimit;
  if (!Number.isSafeInteger(firstRowOffset)) return false;
  if (questions.length > 0) {
    const rowsThrough = firstRowOffset + questions.length;
    if (!Number.isSafeInteger(rowsThrough) || pagination.total < rowsThrough) return false;
  }

  if (options.status?.length) {
    if (!options.status.every(isPublicVisibleStatus)) return false;
    const requestedStatuses = new Set<PublicVisibleStatus>(options.status);
    if (
      !questions.every((question) => requestedStatuses.has(question.status as PublicVisibleStatus))
    ) {
      return false;
    }
  }

  return true;
}

/** Bind a complete detail DTO to the id-or-slug lookup in its request path. */
function isCacheableDetailPayloadForRequest(
  value: unknown,
  requestedQuestion: string
): value is QuestionDetailResponse {
  if (!isCacheableDetailPayload(value) || requestedQuestion.length === 0) return false;

  if (
    value.question.questionId === requestedQuestion ||
    value.question.slug === requestedQuestion
  ) {
    return true;
  }

  // QuestionService also accepts historical/noncanonical slugs by extracting
  // their fixed-length `q_` id prefix. Bind to the same stable identity rather
  // than requiring the response's CURRENT slug to equal an old inbound slug.
  // This mirrors the existing route without narrowing arbitrary exact-id or
  // exact-current-slug compatibility above.
  const questionIdLength = ID_PREFIXES.QUESTION.length + ID_LENGTHS.QUESTION;
  if (
    requestedQuestion.startsWith(ID_PREFIXES.QUESTION) &&
    requestedQuestion.length >= questionIdLength
  ) {
    return value.question.questionId === requestedQuestion.slice(0, questionIdLength);
  }

  return false;
}

// =============================================================================
// CACHE KEYS
// =============================================================================

/**
 * Canonical key for a list read.
 *
 * Composition — every component is required to prevent a real collision:
 *   `<contract version>|<origin + tenant scope>|list|<canonical endpoint>`
 *
 * The endpoint segment is produced by the transport's own
 * {@link buildPublicListEndpoint}, so the key cannot drift from the request.
 * The scope segment carries the API origin and store hash because tenant
 * identity travels as a header, not in the path — without it, two stores on one
 * page would share entries.
 *
 * Contains no credential, capability, cookie, token, email, correlation id, or
 * response content.
 */
export function publicListCacheKey(options: PublicQuestionListOptions = {}): string {
  return `${PUBLIC_READ_CACHE_KEY_VERSION}|${getPublicReadScope()}|list|${buildPublicListEndpoint(options)}`;
}

/** Canonical key for a detail read. See {@link publicListCacheKey}. */
export function publicDetailCacheKey(questionId: string): string {
  return `${PUBLIC_READ_CACHE_KEY_VERSION}|${getPublicReadScope()}|detail|${buildPublicDetailEndpoint(questionId)}`;
}

// =============================================================================
// RESOURCE
// =============================================================================

/** Freshness classification of a cache lookup. */
export type CacheFreshness = 'fresh' | 'stale' | 'miss';

/** Result of a synchronous, render-safe cache lookup. */
export interface CachePeek<T> {
  /** Retained payload, or `null` when there is nothing usable to render. */
  data: T | null;
  /**
   * `fresh` — render it, issue nothing.
   * `stale` — render it, revalidate in the background.
   * `miss`  — nothing usable (absent OR past `MAX_AGE_MS`); load blocking.
   */
  freshness: CacheFreshness;
}

interface CacheEntry {
  data: unknown;
  storedAt: number;
  bytes: number;
  /** Timestamp of the last FAILED revalidation, for cooldown. */
  failedAt: number | null;
}

interface FlightOutcome {
  response: ApiResponse<unknown>;
  /** True only when a strict success envelope passed its DTO guard. */
  validated: boolean;
  /** True only when that validated payload became the current cache entry. */
  admitted: boolean;
  /** The admitted DTO, otherwise null; avoids re-narrowing an untrusted envelope. */
  data: unknown | null;
}

interface Flight {
  promise: Promise<FlightOutcome>;
  controller: AbortController;
  /** Invalidation epoch captured when the flight started. */
  epoch: number;
}

/** Fetcher contract: given a signal, perform exactly one network read. */
type Fetcher<T> = (signal: AbortSignal) => Promise<ApiResponse<T>>;

/** Safe result exposed to stale-while-revalidate consumers. */
export type CacheRevalidationResult<T> =
  | { admitted: true; data: T }
  | { admitted: false; data: null };

export interface PublicQuestionResource {
  peek<T>(key: string): CachePeek<T>;
  load<T>(
    key: string,
    fetcher: Fetcher<T>,
    validate: (value: unknown) => boolean,
    options?: { force?: boolean }
  ): Promise<ApiResponse<T>>;
  /** Returns an admission-aware shared result, or `null` when declining. */
  revalidate<T>(
    key: string,
    fetcher: Fetcher<T>,
    validate: (value: unknown) => boolean
  ): Promise<CacheRevalidationResult<T>> | null;
  invalidateAll(): void;
  /** Diagnostics for tests and reasoning. Never used for rendering. */
  stats(): { entries: number; totalBytes: number; inFlight: number; epoch: number };
}

/**
 * Build an independent resource instance.
 *
 * `now` is a constructor parameter rather than a mutable module global so tests
 * can drive time deterministically without the bundle shipping a test-only
 * escape hatch that production code could reach.
 */
export function createPublicQuestionResource(
  options: { now?: () => number } = {}
): PublicQuestionResource {
  const sourceNow = options.now ?? (() => Date.now());
  let lastSourceNow: number | null = null;
  let logicalNow = 0;

  // Wall clocks can move backwards after an OS/NTP correction. Accumulate only
  // positive source-clock deltas into a logical clock: a rollback contributes
  // no negative age, but becomes the new source anchor so elapsed time starts
  // advancing immediately from the corrected value. A simple high-water clamp
  // would freeze TTL/cooldown progress until the wall clock caught back up.
  const now = (): number => {
    const observed = sourceNow();
    if (!Number.isFinite(observed)) return logicalNow;

    if (lastSourceNow === null) {
      lastSourceNow = observed;
      logicalNow = observed;
      return logicalNow;
    }

    const elapsed = observed - lastSourceNow;
    lastSourceNow = observed;
    if (elapsed > 0 && Number.isFinite(elapsed)) logicalNow += elapsed;
    return logicalNow;
  };

  /**
   * Insertion-ordered map used as an LRU. A read HIT re-inserts its key so it
   * moves to the most-recent end; eviction always takes `keys().next()`, the
   * genuine least-recently-used entry.
   */
  const entries = new Map<string, CacheEntry>();
  const flights = new Map<string, Flight>();
  let totalBytes = 0;
  let epoch = 0;

  function removeEntry(key: string): void {
    const existing = entries.get(key);
    if (!existing) return;
    // Byte accounting is adjusted in the SAME step as the map mutation, so a
    // replacement or an invalidation can never leave `totalBytes` drifting from
    // the retained set.
    totalBytes -= existing.bytes;
    entries.delete(key);
  }

  function evictWhileOverCapacity(): void {
    while (
      entries.size > PUBLIC_READ_CACHE_POLICY.MAX_ENTRIES ||
      totalBytes > PUBLIC_READ_CACHE_POLICY.MAX_TOTAL_BYTES
    ) {
      const oldest = entries.keys().next();
      if (oldest.done) break;
      removeEntry(oldest.value);
    }
  }

  /** Returns true only if the payload actually became cache state. */
  function admit(key: string, data: unknown): boolean {
    const bytes = measureLogicalBytes(data);
    // Unmeasurable or individually oversized: the caller still receives the
    // response, it simply never becomes cache state. Any entry already retained
    // for this key is LEFT ALONE — it is still valid data the visitor can be
    // shown, and deleting it would replace a usable stale render with a
    // blocking skeleton on every subsequent navigation.
    if (bytes === null || bytes > PUBLIC_READ_CACHE_POLICY.MAX_ENTRY_BYTES) {
      return false;
    }

    removeEntry(key);
    entries.set(key, { data, storedAt: now(), bytes, failedAt: null });
    totalBytes += bytes;
    evictWhileOverCapacity();
    return true;
  }

  function noteRevalidationFailure(
    key: string,
    epochAtStart: number,
    entryAtStart: CacheEntry | null
  ): void {
    // A detached pre-invalidation flight belongs to an older cache generation.
    // It must not arm the cooldown on a newer same-key entry that happened to
    // complete first. Exact entry identity also protects against any future
    // same-epoch replacement path.
    if (epochAtStart !== epoch || entryAtStart === null) return;
    const existing = entries.get(key);
    if (existing === entryAtStart) existing.failedAt = now();
  }

  function peek<T>(key: string): CachePeek<T> {
    const entry = entries.get(key);
    if (!entry) return { data: null, freshness: 'miss' };

    const age = now() - entry.storedAt;
    if (age >= PUBLIC_READ_CACHE_POLICY.MAX_AGE_MS) {
      // Hard expiry: drop it and force a blocking load. Expired data is never
      // painted, so a visitor cannot see minutes-old content.
      removeEntry(key);
      return { data: null, freshness: 'miss' };
    }

    // Reading touches recency, so an entry the visitor keeps returning to
    // survives eviction ahead of one they opened once.
    entries.delete(key);
    entries.set(key, entry);

    return {
      data: entry.data as T,
      freshness: age < PUBLIC_READ_CACHE_POLICY.FRESH_TTL_MS ? 'fresh' : 'stale',
    };
  }

  function startFlight<T>(
    key: string,
    fetcher: Fetcher<T>,
    validate: (value: unknown) => boolean
  ): Flight {
    const existing = flights.get(key);
    // Same canonical key -> one shared request. Distinct keys stay independent.
    if (existing) return existing;

    const controller = new AbortController();
    const epochAtStart = epoch;
    const entryAtStart = entries.get(key) ?? null;

    // Issue the request SYNCHRONOUSLY so `load()` returning implies the network
    // call has already been made. Deferring it to a microtask would widen the
    // window in which a second caller could miss the in-flight entry. A
    // synchronous throw from the fetcher is normalised into a rejection so it
    // still flows through the shared failure handling below.
    let started: Promise<ApiResponse<T>>;
    try {
      started = fetcher(controller.signal);
    } catch (error) {
      started = Promise.reject(error);
    }

    const flight: Flight = {
      controller,
      epoch: epochAtStart,
      promise: started
        .then((response) => {
          const strictSuccess = isRecord(response) && response.success === true;
          const validated = strictSuccess && validate(response.data);
          const admissible =
            validated &&
            // A mutation landed while this read was in flight. Admitting now
            // would silently undo the invalidation with pre-mutation data, so
            // the response is returned to the caller but fenced out of cache.
            epochAtStart === epoch;

          // `admissible` is the decision to TRY; `admit` reports whether the
          // payload actually became cache state. They differ for an oversized or
          // unserialisable payload, which passes every predicate and is still
          // declined — so the cooldown must key off the real outcome, not the
          // intent, or such a response would re-request on every stale render.
          const admitted = admissible && admit(key, response.data);
          if (!admitted) {
            // EVERY non-admission arms the cooldown, not just failure
            // envelopes. Errors, timeouts, aborts (which surface as a
            // REQUEST_TIMEOUT envelope), offline and protocol failures land
            // here — but so does a `success: true` response that the admission
            // guard rejects, and an oversized or unserialisable payload. Those
            // succeeded at the transport layer while still yielding nothing
            // cacheable, so if they did not arm the cooldown the entry would
            // stay stale forever and every stale render would issue a fresh
            // request: exactly the storm the cooldown exists to bound.
            // Nothing here admits, and nothing overwrites retained stale data.
            noteRevalidationFailure(key, epochAtStart, entryAtStart);
          }
          return {
            response: response as ApiResponse<unknown>,
            validated,
            admitted,
            data: admitted ? response.data : null,
          };
        })
        .catch((error: unknown) => {
          noteRevalidationFailure(key, epochAtStart, entryAtStart);
          throw error;
        })
        .finally(() => {
          // Identity-safe cleanup: only delete the map slot if it still holds
          // THIS flight. A late completion must never remove a newer flight
          // that replaced it.
          if (flights.get(key) === flight) flights.delete(key);
        }),
    };

    flights.set(key, flight);
    return flight;
  }

  function load<T>(
    key: string,
    fetcher: Fetcher<T>,
    validate: (value: unknown) => boolean,
    loadOptions: { force?: boolean } = {}
  ): Promise<ApiResponse<T>> {
    if (!loadOptions.force) {
      const hit = peek<T>(key);
      // Fresh: satisfy entirely from memory, issue nothing at all.
      if (hit.freshness === 'fresh' && hit.data !== null) {
        return Promise.resolve({ success: true, data: hit.data });
      }
    }
    return startFlight(key, fetcher, validate).promise.then(({ response, validated }) => {
      // Never pass a malformed `success: true` payload to a cold-path consumer:
      // it is just as capable of crashing the current render as a replayed cache
      // entry. Valid-but-oversized and epoch-fenced responses remain usable by
      // their original caller; only runtime contract failures are normalised.
      if (isRecord(response) && response.success === true && !validated) {
        return protocolErrorResponse<T>(
          'The public question response did not match the expected contract.'
        );
      }
      if (
        !isRecord(response) ||
        (response.success !== true && response.success !== false) ||
        (response.success === false && !isStructuredFailureEnvelope(response))
      ) {
        return protocolErrorResponse<T>('The public question response envelope was malformed.');
      }
      return response as ApiResponse<T>;
    });
  }

  /**
   * Refresh a stale entry in the background.
   *
   * Returns the shared flight's promise when a revalidation is in progress (so a
   * caller can adopt the refreshed payload), or `null` when this call is
   * deliberately declining to issue one — no retained entry, or the
   * failed-revalidation cooldown is still active.
   *
   * Returning the promise is what keeps the cooldown enforceable. An earlier
   * shape returned `void`, which forced the caller to follow up with a separate
   * `load()` to obtain the result — and `load()` does not consult the cooldown,
   * so a persistently failing key was re-requested on EVERY stale render. A
   * caller must therefore never pair this with `load()`; it must use what this
   * returns.
   */
  function revalidate<T>(
    key: string,
    fetcher: Fetcher<T>,
    validate: (value: unknown) => boolean
  ): Promise<CacheRevalidationResult<T>> | null {
    const entry = entries.get(key);
    // Revalidation is only meaningful for a retained entry.
    if (!entry) return null;
    // At most one in-flight revalidation per key: join the existing one.
    const existing = flights.get(key);
    if (existing) {
      return existing.promise.then(
        (outcome) =>
          outcome.admitted
            ? { admitted: true, data: outcome.data as T }
            : { admitted: false, data: null },
        () => ({ admitted: false, data: null })
      );
    }
    // Bound retries after a failure.
    if (
      entry.failedAt !== null &&
      now() - entry.failedAt < PUBLIC_READ_CACHE_POLICY.FAILED_REVALIDATION_COOLDOWN_MS
    ) {
      return null;
    }

    // Convert every transport/protocol failure into a non-admitted result. This
    // makes the background API non-throwing and prevents a caller's derived
    // `.then()` chain from creating an unhandled rejection.
    return startFlight(key, fetcher, validate).promise.then(
      (outcome) =>
        outcome.admitted
          ? { admitted: true, data: outcome.data as T }
          : { admitted: false, data: null },
      () => ({ admitted: false, data: null })
    );
  }

  function invalidateAll(): void {
    // Bumping the epoch fences every CURRENTLY IN-FLIGHT read: each captured
    // the previous epoch and will refuse admission on completion. Without this,
    // a read that started before the visitor's submission could land after the
    // clear and restore exactly the state we just invalidated.
    epoch += 1;
    entries.clear();
    totalBytes = 0;
    // Nothing from the previous generation can be served or annotated after
    // the epoch bump, so its wall-clock high-water mark is no longer relevant.
    // Resetting lets a new generation establish a fresh origin after an OS
    // clock correction and keeps deterministic/fake clocks isolated across a
    // full invalidation. Detached old flights are epoch-fenced before they can
    // consult `now()` or mutate the replacement generation.
    lastSourceNow = null;
    logicalNow = 0;

    // Detach the in-flight reads too. Those requests were issued BEFORE the
    // mutation, so their responses are pre-mutation data: they are already
    // fenced out of admission by the epoch, but a caller arriving after the
    // invalidation must not be handed one of them by de-duplication. Clearing
    // the map means the next caller starts a genuinely fresh request.
    //
    // The existing flights are deliberately NOT aborted. Their original
    // awaiters committed to that read before the mutation and are better served
    // by a completed response than by a spurious cancellation error, and the
    // identity-safe `finally` below already prevents a detached flight from
    // deleting the newer flight that replaces it.
    flights.clear();
  }

  return {
    peek,
    load,
    revalidate,
    invalidateAll,
    stats: () => ({
      entries: entries.size,
      totalBytes,
      inFlight: flights.size,
      epoch,
    }),
  };
}

// =============================================================================
// SESSION-SCOPED INSTANCE + TYPED READS
// =============================================================================

/**
 * The document-lifetime instance every hook shares. Module scope IS the session
 * scope: it survives same-document routing (and a BFCache restore, where the
 * script context is retained) and dies on a real navigation. There is no
 * persistence layer beneath it by design.
 */
const publicQuestionResource = createPublicQuestionResource();

/** Synchronous, render-safe list lookup used to seed hook state without a flash. */
export function peekPublicQuestionList(
  options: PublicQuestionListOptions = {}
): CachePeek<QuestionListResponse> {
  return publicQuestionResource.peek<QuestionListResponse>(publicListCacheKey(options));
}

/** Synchronous, render-safe detail lookup. */
export function peekPublicQuestionDetail(questionId: string): CachePeek<QuestionDetailResponse> {
  return publicQuestionResource.peek<QuestionDetailResponse>(publicDetailCacheKey(questionId));
}

/**
 * Load a public list, sharing one request per canonical key.
 *
 * `force: true` (explicit visitor retry) bypasses the freshness check so the
 * visitor always gets a real network attempt, but still joins an identical
 * in-flight request rather than doubling it.
 */
export function loadPublicQuestionList(
  options: PublicQuestionListOptions = {},
  loadOptions: { force?: boolean } = {}
): Promise<ApiResponse<QuestionListResponse>> {
  return publicQuestionResource.load<QuestionListResponse>(
    publicListCacheKey(options),
    (signal) => widgetApi.questions.getPublic({ ...options, signal }),
    (value) => isCacheableListPayloadForRequest(value, options),
    loadOptions
  );
}

/**
 * Kick at most one deduped background revalidation for a stale list entry.
 *
 * Returns the shared flight to adopt, or `null` when declining. Callers must use
 * the returned promise rather than following up with {@link loadPublicQuestionList},
 * which does not consult the failed-revalidation cooldown.
 */
export function revalidatePublicQuestionList(
  options: PublicQuestionListOptions = {}
): Promise<CacheRevalidationResult<QuestionListResponse>> | null {
  return publicQuestionResource.revalidate<QuestionListResponse>(
    publicListCacheKey(options),
    (signal) => widgetApi.questions.getPublic({ ...options, signal }),
    (value) => isCacheableListPayloadForRequest(value, options)
  );
}

/** Load a public question detail, sharing one request per canonical key. */
export function loadPublicQuestionDetail(
  questionId: string,
  loadOptions: { force?: boolean } = {}
): Promise<ApiResponse<QuestionDetailResponse>> {
  return publicQuestionResource.load<QuestionDetailResponse>(
    publicDetailCacheKey(questionId),
    (signal) => widgetApi.questions.getDetail(questionId, { signal }),
    (value) => isCacheableDetailPayloadForRequest(value, questionId),
    loadOptions
  );
}

/**
 * Kick at most one deduped background revalidation for a stale detail entry.
 * See {@link revalidatePublicQuestionList} for the return contract.
 */
export function revalidatePublicQuestionDetail(
  questionId: string
): Promise<CacheRevalidationResult<QuestionDetailResponse>> | null {
  return publicQuestionResource.revalidate<QuestionDetailResponse>(
    publicDetailCacheKey(questionId),
    (signal) => widgetApi.questions.getDetail(questionId, { signal }),
    (value) => isCacheableDetailPayloadForRequest(value, questionId)
  );
}

/**
 * Drop every retained public read and fence in-flight ones.
 *
 * Called at the DURABLE creation point of the visitor's own question or reply —
 * before optional image uploads, because the record exists as soon as the
 * create succeeds and a partial photo outcome does not undo it. A FAILED create
 * must not call this: nothing changed server-side.
 *
 * This clears cached state so the next read is authoritative. It never inserts
 * the new content, which is pending moderation and must not be represented as
 * published.
 */
export function invalidatePublicReads(): void {
  publicQuestionResource.invalidateAll();
}

/** Diagnostics for tests. Not used by any rendering path. */
export function publicReadCacheStats(): ReturnType<PublicQuestionResource['stats']> {
  return publicQuestionResource.stats();
}
